import React, {FC, useEffect, useMemo, useRef, useState} from "react";
import {Button, Col, Container, FormSelect, InputGroup, Row, Spinner} from "react-bootstrap";
import {MaybeDate} from "../../types/Maybe";
import {
    dateTimeToString,
    dateToString,
    dateWithZeroTime, getDateAsPeriod,
} from "../../tools";
import ReactDatePicker from "react-datepicker";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCalendar, faFileExcel} from "@fortawesome/free-solid-svg-icons";
import RRTable from "../../components/rr-table/RRTable";
import {collection, getDocs, orderBy, query, where} from "firebase/firestore";
import {appFirestore} from "../../../index";
import {RRCalendarDaysType} from "../../types/RRCalendarDayType";
import {calendarDataConverter} from "../../../firebase/FirebaseCalendarLoader";
import {useAppSelector} from "../../hooks";
import {selectEvents} from "../../../features/AppDataSlice";
import {selectDepartment} from "../../../features/AppStateSlice";
import {utils, writeFile} from "xlsx";
import {getReportMonthOptions} from "./reportMonthOptions";

type ReportItemType = {
    emplId: string;
    emplName: string;
    date: string;
    events: RRCalendarDaysType;
    hasOverlapping: boolean;
}

const ReportTimeDuplicatesView: FC = () => {
    const department = useAppSelector(selectDepartment);
    const today = dateWithZeroTime(new Date())!;
    const monthOptions = useMemo(() => getReportMonthOptions(today), [today]);
    const defaultMonthOption = monthOptions[1] ?? monthOptions[0];

    const [working, setWorking] = useState(false);
    const [periodFrom, setPeriodFrom] = useState<MaybeDate>();
    const [periodTo, setPeriodTo] = useState<MaybeDate>();
    const [selectedMonth, setSelectedMonth] = useState<string>(defaultMonthOption.value);
    const [dpPeriodFrom, setDpPeriodFrom] = useState<MaybeDate>(defaultMonthOption.periodFrom);
    const [dpPeriodTo, setDpPeriodTo] = useState<MaybeDate>(defaultMonthOption.periodTo);
    const [results, setResults] = useState<RRCalendarDaysType | undefined>(undefined);
    const [report, setReport] = useState<ReportItemType[] | undefined>(undefined);
    const tblRef = useRef<HTMLTableElement>(null);

    useEffect(() => {
        if (periodFrom && periodTo) {

            const constr = [
                where("eventDayEnd", ">=", periodFrom),
                where("eventDayEnd", "<", periodTo),
                where("department", "==", department)
            ];

            try {
                setWorking(true);
                getDocs(query(collection(appFirestore, "calendar"), ...constr
                )).then(
                    calendarSnapshot => {
                        setWorking(false);
                        setResults(calendarDataConverter(calendarSnapshot.docs))
                    }
                );
            } catch (e) {
                console.error(e)
            }
        }
    }, [periodFrom, periodTo, department]);

    function setRefPeriod() {
        setPeriodFrom(dateWithZeroTime(dpPeriodFrom));
        const [_f, _t] = getDateAsPeriod(dpPeriodTo!);
        setPeriodTo(dateWithZeroTime(_t));
    }

    // Изборът на месец попълва началната и крайната дата с целия съответен период.
    function applyMonthSelection(optionValue: string) {
        const selectedOption = monthOptions.find(option => option.value === optionValue);
        if (!selectedOption) {
            return;
        }

        setSelectedMonth(selectedOption.value);
        setDpPeriodFrom(selectedOption.periodFrom);
        setDpPeriodTo(selectedOption.periodTo);
    }

    useEffect(() => {
        if (results) {
            const _report: ReportItemType[] = [];
            results.forEach(
                r => {
                    const _empl = _report.find(e => e.emplId === r.employeeId && e.date === dateToString(r.eventDayStart));
                    if (_empl) {
                        _empl.events.push(r);
                        if (!_empl.hasOverlapping && _empl.events.length > 1) {
                            _empl.events
                                .filter(e => e.eventId !== r.eventId)
                                .forEach(
                                    e => {
                                        if (e.eventDayEnd && r.eventDayEnd) {
                                            if (e.eventDayStart < r.eventDayStart && e.eventDayEnd > r.eventDayStart) {
                                                _empl.hasOverlapping = true;
                                            }
                                            if (e.eventDayStart < r.eventDayEnd && e.eventDayEnd > r.eventDayEnd) {
                                                _empl.hasOverlapping = true;
                                            }
                                            if (e.eventDayStart === r.eventDayStart && e.eventDayEnd === r.eventDayEnd) {
                                                _empl.hasOverlapping = true;
                                            }
                                        }
                                    }
                                );
                        }
                    } else {
                        _report.push({
                            emplId: r.employeeId,
                            emplName: r.employeeName,
                            date: dateToString(r.eventDayStart)!,
                            events: [r],
                            hasOverlapping: false,
                        });
                    }
                }
            );
            setReport(_report.filter(r => r.hasOverlapping));
        }
    }, [results]);



    function exportExcel(): void {
        const workbook = utils.table_to_book(tblRef.current,);
        writeFile(workbook, "Report Time Duplicates.xlsx");
    }

    return <Container fluid>
        <h1>Справкa за дублирани часове за период</h1>

        <Row className={"g-2 mt-3 flex-nowrap"}>
            <Col>
                <Row className={"lh-2-5em flex-nowrap"}>
                    <Col xs={"auto"}>
                        <Row>
                            <Col xs={"auto"} className={"lh-2-5em"}>
                                Месец:
                            </Col>
                            <Col xs={"auto"} className={"d-inline-block"}>
                                <FormSelect
                                    className={"mb-2"}
                                    disabled={working}
                                    value={selectedMonth}
                                    onChange={event => applyMonthSelection(event.target.value)}
                                >
                                    {
                                        monthOptions.map(option => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))
                                    }
                                </FormSelect>
                            </Col>
                        </Row>
                    </Col>
                    <Col xs={"auto"}>
                        <Row>
                            <Col xs={"auto"} className={"lh-2-5em"}>
                                Начална дата:
                            </Col>
                            <Col xs={"auto"} className={"d-inline-block"}>
                                <InputGroup className="mb-2 w-180px flex-nowrap">
                                    <ReactDatePicker
                                        disabled={working}
                                        id={"periodFromDate"}
                                        className={"form-control flex-nowrap"}
                                        selected={dpPeriodFrom}
                                        onChange={date => setDpPeriodFrom(date || defaultMonthOption.periodFrom)}
                                        value={dateToString(dpPeriodFrom)}
                                        maxDate={dpPeriodTo}
                                    />
                                    <label htmlFor={"periodFromDate"}>
                                        <InputGroup.Text className={"lh-2-5em"}>
                                            <FontAwesomeIcon icon={faCalendar} size={"xl"}/>
                                        </InputGroup.Text>
                                    </label>
                                </InputGroup>
                            </Col>
                        </Row>
                    </Col>
                    <Col xs={"auto"}>
                        <Row>
                            <Col xs={"auto"} className={"lh-2-5em"}>
                                Крайна дата:
                            </Col>
                            <Col xs={"auto"} className={"d-inline-block"}>
                                <InputGroup className="mb-2 w-180px flex-nowrap">
                                    <ReactDatePicker
                                        disabled={working}
                                        id={"periodToDate"}
                                        className={"form-control flex-nowrap"}
                                        selected={dpPeriodTo}
                                        onChange={date => setDpPeriodTo(date || today)}
                                        value={dateToString(dpPeriodTo)}
                                        minDate={dpPeriodFrom}
                                    />
                                    <label htmlFor={"periodToDate"}>
                                        <InputGroup.Text className={"lh-2-5em"}>
                                            <FontAwesomeIcon icon={faCalendar} size={"xl"}/>
                                        </InputGroup.Text>
                                    </label>
                                </InputGroup>
                            </Col>
                        </Row>
                    </Col>
                    <Col>
                        {
                            !working && <>
                                <Button onClick={setRefPeriod} disabled={!dpPeriodFrom || !dpPeriodTo}>Изчисли</Button>&nbsp;
                                <Button onClick={() => exportExcel()} variant={"outline-success"}><FontAwesomeIcon
                                    icon={faFileExcel}/></Button>&nbsp;
                            </>
                        }
                    </Col>
                </Row>
            </Col>
        </Row>

        <Row className={"g-2 mt-3"}>
            <Col>
                {
                    report && !working ?
                        <RRTable ref={tblRef}>
                            <thead>
                            <tr>
                                <th>Служител</th>
                                <th>Дата</th>
                                <th>Часове</th>
                            </tr>
                            </thead>

                            <tbody>
                            {
                                report.map(
                                    r => {
                                        return <tr key={`${r.emplId}-${r.date}`}>
                                            <td> {r.emplName} </td>
                                            <td> {r.date} </td>
                                            <td>
                                                {
                                                    r.events.map(
                                                        e => {
                                                            return <div key={e.eventId}>
                                                                {dateTimeToString(e.eventDayStart)} -&gt; {dateTimeToString(e.eventDayEnd)};&nbsp;
                                                            </div>
                                                        }
                                                    )
                                                }
                                            </td>
                                        </tr>
                                    }
                                )
                            }
                            </tbody>
                        </RRTable> : <strong>
                            {
                                working ? <Row>
                                    <Col className={"text-center text-dark"}>
                                        <Spinner animation={"border"} />
                                    </Col></Row> : "Моля, изберете период и натиснете \"Изчисли\"..."
                            }
                        </strong>
                }
            </Col>
        </Row>

    </Container>
}

export default ReportTimeDuplicatesView;
